import { create } from 'zustand';
import { createJSONStorage, persist } from 'zustand/middleware';

/**
 * Generic sidenav highlight store — keyed by nav item ID.
 *
 * Any feature can request a tooltip highlight on a sidenav item by calling
 * `useNavHighlightStore.getState().setHighlight('certificates')`. The sidenav
 * reads `hasHighlight(key)` and dismisses via `clearHighlight(key)`.
 * Persisted to localStorage so the highlight survives page navigations and
 * refreshes.
 *
 * @example
 * // Trigger (after task form submit):
 * useNavHighlightStore.getState().setHighlight('certificates');
 *
 * // Read (in sideNavContent):
 * const show = useNavHighlightStore((s) => s.hasHighlight('certificates'));
 *
 * // Dismiss (on landing page):
 * const clear = useNavHighlightStore((s) => s.clearHighlight);
 * useEffect(() => { clear('certificates'); }, [clear]);
 */
interface NavHighlightState {
  highlights: Record<string, boolean>;
  setHighlight: (key: string) => void;
  clearHighlight: (key: string) => void;
  hasHighlight: (key: string) => boolean;
}

export const useNavHighlightStore = create<NavHighlightState>()(
  persist(
    (set, get) => ({
      highlights: {},
      setHighlight: (key) =>
        set((s) => ({ highlights: { ...s.highlights, [key]: true } })),
      clearHighlight: (key) =>
        set((s) => ({ highlights: { ...s.highlights, [key]: false } })),
      hasHighlight: (key) => get().highlights[key] === true,
    }),
    {
      name: 'nav-highlight',
      storage: createJSONStorage(() => localStorage),
      partialize: (s) => ({ highlights: s.highlights }),
    }
  )
);
